import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getExamDate, upsertUserProfile } from '../../services/supabaseService';
import type { Language } from '../../types_i18n';
import './ExamDateScreen.css';

const TEXTS: Record<Language, {
  title: string;
  subtitle: string;
  monthLabel: string;
  dayLabel: string;
  yearLabel: string;
  primary: string;
  skip: string;
  months: string[];
  placeholderMonth: string;
  placeholderDay: string;
  placeholderYear: string;
}> = {
  nl: {
    title: 'Wanneer is je examen?',
    subtitle: 'Hoe meer we weten, hoe gerichter je kunt oefenen. We bepalen hoeveel tijd je nog hebt.',
    monthLabel: 'MAAND',
    dayLabel: 'DAG',
    yearLabel: 'JAAR',
    months: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
    primary: 'Laat me mijn studieplan zien',
    skip: 'Ik weet het nog niet',
    placeholderMonth: 'Maand',
    placeholderDay: 'Dag',
    placeholderYear: 'Jaar',
  },
  en: {
    title: 'When is your exam?',
    subtitle: 'The more we know, the better we can guide your study. We\'ll figure out how much time you have left.',
    monthLabel: 'MONTH',
    dayLabel: 'DAY',
    yearLabel: 'YEAR',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    primary: 'Let me see my study plan',
    skip: 'I don\'t know yet',
    placeholderMonth: 'Month',
    placeholderDay: 'Day',
    placeholderYear: 'Year',
  },
  ar: {
    title: 'متى موعد امتحانك؟',
    subtitle: 'كلما عرفنا أكثر، كان بإمكاننا توجيه دراستك بشكل أفضل. سنحدد المدة المتبقية لديك.',
    monthLabel: 'الشهر',
    dayLabel: 'اليوم',
    yearLabel: 'السنة',
    months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
    primary: 'دعني أرى خطتي الدراسية',
    skip: 'لا أعلم بعد',
    placeholderMonth: 'الشهر',
    placeholderDay: 'اليوم',
    placeholderYear: 'السنة',
  },
};

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Day numbers (1–lastOfMonth) that are today or in the future for the given calendar month/year. */
function getAvailableDayNumbers(yearStr: string, monthStr: string): number[] {
  if (!yearStr || !monthStr) return [];
  const y = parseInt(yearStr, 10);
  const m = parseInt(monthStr, 10);
  if (Number.isNaN(y) || Number.isNaN(m) || m < 1 || m > 12) return [];
  const lastDay = new Date(y, m, 0).getDate();
  const today = startOfToday();
  const out: number[] = [];
  for (let d = 1; d <= lastDay; d++) {
    const candidate = new Date(y, m - 1, d);
    candidate.setHours(0, 0, 0, 0);
    if (candidate >= today) out.push(d);
  }
  return out;
}

/** Infer year for a selected rolling month when year is not explicitly selected yet. */
function inferYearForMonth(
  monthStr: string,
  currentYear: number,
  currentMonthIndex: number
): string {
  const m = parseInt(monthStr, 10);
  if (Number.isNaN(m) || m < 1 || m > 12) return '';
  // Month values lower than the current month in the rolling picker belong to next year.
  return String(m - 1 < currentMonthIndex ? currentYear + 1 : currentYear);
}

export default function ExamDateScreen() {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [checking, setChecking] = useState(true);
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [loading, setLoading] = useState(false);

  const lang = (currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en') as Language;
  const t = TEXTS[lang];

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11
  const endYear = currentMonth + 11 >= 12 ? currentYear + 1 : currentYear;
  const years = endYear > currentYear ? [currentYear, endYear] : [currentYear];

  const getAvailableMonths = () => {
    const monthsArray = t.months;
    const availableMonths = [];
    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonth + i) % 12;
      availableMonths.push({
        index: monthIndex + 1, // 1-12 for option value
        name: monthsArray[monthIndex],
      });
    }
    return availableMonths;
  };

  const effectiveYear = year || inferYearForMonth(month, currentYear, currentMonth);

  const availableDays = useMemo(
    () => getAvailableDayNumbers(effectiveYear, month),
    [effectiveYear, month]
  );

  useEffect(() => {
    if (!month || !effectiveYear) return;
    const allowed = getAvailableDayNumbers(effectiveYear, month);
    if (allowed.length === 0) {
      if (day) setDay('');
      return;
    }
    if (day) {
      const n = parseInt(day, 10);
      if (Number.isNaN(n) || !allowed.includes(n)) setDay('');
    }
  }, [month, effectiveYear, day]);

  useEffect(() => {
    if (!month || year) return;
    const inferredYear = inferYearForMonth(month, currentYear, currentMonth);
    if (inferredYear) {
      setYear(inferredYear);
    }
  }, [month, year, currentYear, currentMonth]);

  useEffect(() => {
    const check = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setChecking(false);
        return;
      }
      try {
        const examDate = await getExamDate(userId);
        if (examDate) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        // fall through to show screen
      }
      setChecking(false);
    };
    check();
  }, [navigate]);

  const handlePrimary = async () => {
    if (!month || !day || !year || loading) return;
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const dNum = parseInt(day, 10);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(dNum)) return;
    const selected = new Date(y, m - 1, dNum);
    selected.setHours(0, 0, 0, 0);
    const today = startOfToday();
    if (selected < today) return;
    if (
      selected.getFullYear() !== y ||
      selected.getMonth() !== m - 1 ||
      selected.getDate() !== dNum
    ) {
      return;
    }
    setLoading(true);
    const selectedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const userId = localStorage.getItem('userId');
    if (userId) {
      const ok = await upsertUserProfile(userId, { exam_date: selectedDate });
      if (ok) {
        setTimeout(() => navigate('/dashboard', { replace: true }), 240);
        return;
      }
      setLoading(false);
    } else {
      try {
        localStorage.setItem('EXAM_DATE_PENDING', selectedDate);
      } catch {
        // ignore
      }
      setTimeout(() => navigate('/dashboard', { replace: true }), 240);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard', { replace: true });
  };

  if (checking) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        background: '#0D1520',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16 }}>...</div>
      </div>
    );
  }

  return (
    <div className="date-screen">
      <button className="onboarding-back-btn" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="20">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <div className="onboarding-step-dots">
        <div className="onboarding-step-dot done" />
        <div className="onboarding-step-dot done" />
        <div className="onboarding-step-dot active" />
      </div>

      <div className="onboarding-content">
        <h1 className="onboarding-title">{t.title}</h1>
        <p className="onboarding-subtitle">{t.subtitle}</p>

        <div className="date-columns">
          <div className="date-column month">
            <span className="date-column-label">{t.monthLabel}</span>
            <select
              className="date-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              <option value="">{t.placeholderMonth}</option>
              {getAvailableMonths().map(({ index, name }) => (
                <option key={index} value={String(index)}>{name}</option>
              ))}
            </select>
          </div>

          <div className="date-column day">
            <span className="date-column-label">{t.dayLabel}</span>
            <select
              className="date-select"
              value={day}
              onChange={(e) => setDay(e.target.value)}
            >
              <option value="">{t.placeholderDay}</option>
              {availableDays.map((d) => (
                <option key={d} value={String(d)}>{String(d).padStart(2, '0')}</option>
              ))}
            </select>
          </div>

          <div className="date-column year">
            <span className="date-column-label">{t.yearLabel}</span>
            <select
              className="date-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="">{t.placeholderYear}</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="onboarding-cta-area">
        <button
          className="onboarding-cta-btn"
          onClick={handlePrimary}
          disabled={!month || !day || !year || availableDays.length === 0 || loading}
        >
          {t.primary}
        </button>
        <button
          className="onboarding-skip-link"
          onClick={handleSkip}
        >
          {t.skip}
        </button>
      </div>
    </div>
  );
}
